/**
 * Company Data Manager for optimizing company data loading and caching
 * Created to address the extremely slow /api/companies/fetchcompanies endpoint (250s+ response time)
 */
import { apiCache } from './cacheManager';
import enhancedApi from './enhancedApi';
import { performanceMonitor } from './performanceMonitor';

// Constants
const COMPANIES_CACHE_KEY = 'all-companies-data';
const COMPANIES_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours by default

// Keep a reference to the last fetch promise to prevent duplicate requests
let activeCompanyFetchPromise = null;

const companyDataManager = {
  /**
   * Fetch companies with enhanced caching and deduplication
   * @param {boolean} forceRefresh - Force a refresh bypassing cache
   * @returns {Promise<Array>} - Companies data
   */
  async fetchCompanies(forceRefresh = false) {
    // Use cached data if available and not forcing refresh
    if (!forceRefresh) {
      const cachedData = apiCache.get(COMPANIES_CACHE_KEY);
      if (cachedData) {
        console.log('📦 Using cached companies data');
        return cachedData;
      }
    }

    // If there's an active fetch in progress, return that promise to prevent duplicate requests
    if (activeCompanyFetchPromise) {
      console.log('🔄 Using existing companies fetch request');
      return activeCompanyFetchPromise;
    }

    // Start performance monitoring
    performanceMonitor.start('fetchCompanies');
    console.log('🌐 Fetching fresh companies data');

    // Create a new fetch promise
    activeCompanyFetchPromise = enhancedApi.get('/api/companies/fetchcompanies', {
      cacheTTL: COMPANIES_CACHE_TTL,
      forceRefresh,
      useDeduplication: true
    }).finally(() => {
      // Clear the active promise reference when done
      activeCompanyFetchPromise = null;
      performanceMonitor.end('fetchCompanies');
    });

    return activeCompanyFetchPromise;
  },

  /**
   * Pre-load companies data on app initialization
   * This can be called during app startup to warm the cache
   */
  preloadCompaniesData() {
    // Use a short timeout to not block initial rendering
    setTimeout(() => {
      this.fetchCompanies(false)
        .then(data => console.log(`✅ Preloaded ${data?.length || 0} companies`))
        .catch(err => console.error('❌ Failed to preload companies:', err));
    }, 2000);
  },

  /**
   * Get a company by ID from the cache if available
   * @param {string} companyId - The company ID to find
   * @returns {Object|null} - Company object or null if not found
   */
  getCompanyById(companyId) {
    const companies = apiCache.get(COMPANIES_CACHE_KEY);
    if (!companies) return null;
    
    return companies.find(company => company._id === companyId || company.id === companyId) || null;
  },

  /**
   * Filter companies based on criteria without fetching again
   * @param {Function} filterFn - Filter function that takes a company and returns boolean
   * @returns {Array} - Filtered companies or empty array if cache not available
   */
  filterCompanies(filterFn) {
    const companies = apiCache.get(COMPANIES_CACHE_KEY);
    if (!companies) return [];
    
    return companies.filter(filterFn);
  },

  /**
   * Clear the companies cache
   */
  clearCompaniesCache() {
    apiCache.delete(COMPANIES_CACHE_KEY);
    console.log('🧹 Companies cache cleared');
  }
};

export default companyDataManager;